import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppUserService } from './app-user.service';
import { AppStateService } from './app-state.service';
import { Store } from '@ngrx/store';
import { AppState } from '../store/states/app.state';
import { selectCurrentUser } from '../store/selectors/user.selector';
import { filter, take } from 'rxjs/operators';
import { Project } from '../entities/organization.entity';
import { HttpAuthHelperService } from './http-auth-helper.service';
import { environment } from 'src/environments/environment';
import { isPublicRoute } from '../helpers/public-route.helper';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private projects: any[] = [];
  private projectsLoaded = false;

  public projects$ = new BehaviorSubject<any[]>([]);
  private selectedProjectSubject = new BehaviorSubject<any>(null);
  selectedProject$ = this.selectedProjectSubject.asObservable();

  private selectedProjectSignal = signal<any>(null);

  /** All projects in the system (for admin list). Fetched via loadAllSystemProjects(). */
  public allSystemProjects$ = new BehaviorSubject<Project[]>([]);

  private readonly baseUrl = environment.endpoint;

  constructor(
    private appUserService: AppUserService,
    private appState: AppStateService,
    private store: Store<AppState>,
    private httpAuthHelperService: HttpAuthHelperService
  ) {

    this.store
      .select(selectCurrentUser)
      .pipe(
        filter((user) => !!user && user.sub !== ''),
        take(1)
      )
      .subscribe(async (user) => {
        if (
          user.role === 'pavcon' ||
          user.role === 'organization-admin' ||
          user.role === 'organization-base' ||
          user.role === 'organization-user'
        ) {
          await this.loadProjects();
        }
      });
  }

  private updateProjectSelection(project: any, skipStorage: boolean = false) {
    if (!project) return;

    this.selectedProjectSignal.set(project);
    this.selectedProjectSubject.next(project.project);
    this.appState.updateSelectedProject(project.project);

    if (!skipStorage) {
      localStorage.setItem('selectedProject', project.project);
    }
  }

  get selectedProject() {
    return this.selectedProjectSignal();
  }

  async loadProjects() {
    if (isPublicRoute() || this.projectsLoaded) return;

    try {
      const userDetails = await this.appUserService.getUserDetails();
      if (userDetails && userDetails.projects) {
        this.projects = userDetails.projects.map((p: any) => ({
          project: p,
        }));

        this.projects$.next(this.projects);

        if (this.projects.length > 0) {
          const savedProject = localStorage.getItem('selectedProject');
          if (savedProject) {
            const found = this.projects.find((p) => p.project === savedProject);
            if (found) {
              this.updateProjectSelection(found, true);
            } else {
              this.updateProjectSelection(this.projects[0], true);
            }
          } else {
            this.updateProjectSelection(this.projects[0], true);
          }
        }

        this.projectsLoaded = true;
      }
    } catch {
      // Admin project loading is not available on public routes or when offline.
    }
  }

  ensureLoaded(): Promise<void> {
    return this.loadProjects();
  }

  setSelectedProject(project: any) {
    if (!project) return;
    this.updateProjectSelection(project);
  }

  initializeSelectedProject() {
    if (this.projects.length === 0) return;

    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject) {
      const found = this.projects.find((p) => p.project === savedProject);
      if (found) {
        this.updateProjectSelection(found, true);
        return;
      }
    }

    if (!this.selectedProject) {
      this.updateProjectSelection(this.projects[0], true);
    }
  }

  /**
   * Loads all projects configured in the system (from get-projects API).
   * Result is flattened to Project[] and emitted on allSystemProjects$.
   */
  async loadAllSystemProjects(): Promise<void> {
    try {
      const result =
        (await this.appUserService.getOrganizationProjects()) as unknown as
          | Project[]
          | Record<string, Project[]>;
      const flatList: Project[] = Array.isArray(result)
        ? result
        : (Object.values(result) as Project[][]).flat();
      this.allSystemProjects$.next(flatList);
    } catch (err) {
      this.allSystemProjects$.next([]);
      throw err;
    }
  }

  async getAdminAllProjects(): Promise<Project[]> {
    const data = await this.httpAuthHelperService.requestPromise(
      'GET',
      `${this.baseUrl}/admin-projects/all`,
      {}
    );
    if (data?.body?.response && Array.isArray(data.body.response)) {
      return data.body.response as Project[];
    }
    return [];
  }

  async getAdminAllProjectsFromCentralized(): Promise<Project[]> {
    const data = await this.httpAuthHelperService.requestPromise(
      'GET',
      `${this.baseUrl}/admin-projects/centralized/all`,
      {}
    );
    if (data?.body?.response && Array.isArray(data.body.response)) {
      return data.body.response as Project[];
    }
    return [];
  }

  projectKey(p: Project): string {
    return `${p.project}|${p.organization ?? ''}`;
  }

  async getProjectMappings(): Promise<Record<string, Project>> {
    return {};
  }

  async createAdminProject(
    organization: string,
    project: string,
    project_number?: string,
    project_lead?: string,
    program?: string
  ): Promise<Project> {
    const payload: {
      organization: string;
      project: string;
      project_number?: string;
      project_lead?: string;
      program?: string;
      is_active?: boolean;
    } = {
      organization: organization?.trim(),
      project: project?.trim(),
      is_active: true,
    };
    if (project_number != null && String(project_number).trim() !== '') {
      payload.project_number = String(project_number).trim();
    }
    if (project_lead != null && String(project_lead).trim() !== '') {
      payload.project_lead = String(project_lead).trim();
    }
    if (program != null && String(program).trim() !== '') {
      payload.program = String(program).trim();
    }
    const data = await this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/admin-projects/create`,
      payload
    );

    if (data?.body?.success === false) {
      throw new Error(
        data?.body?.error || data?.body?.message || 'Project creation failed'
      );
    }
    if (data?.body?.response) {
      return data.body.response as Project;
    }
    return {
      organization: payload.organization ?? '',
      project: payload.project ?? '',
      project_number: payload.project_number,
      project_lead: project_lead ?? undefined,
      program: payload.program,
    };
  }

  async updateAdminProject(
    onPlatform: Project,
    oem_project:
      | Project
      | {
          project_number?: string;
          project_lead?: string;
          program?: string;
          is_active?: boolean;
          organization?: string;
        }
      | null,
    opts?: { old_organization?: string; organization?: string }
  ): Promise<{ success: boolean; error?: string }> {
    const project_number =
      oem_project != null && (oem_project.project_number ?? '').trim() !== ''
        ? String(oem_project.project_number).trim()
        : null;
    const project_lead =
      oem_project != null && (oem_project.project_lead ?? '').trim() !== ''
        ? String(oem_project.project_lead).trim()
        : undefined;
    const program =
      oem_project != null && (oem_project.program ?? '').trim() !== ''
        ? String(oem_project.program).trim()
        : undefined;
    const is_active: boolean =
      oem_project != null && 'is_active' in oem_project
        ? (oem_project.is_active ?? true)
        : true;

    const newOrganization =
      opts?.organization?.trim() ?? onPlatform.organization ?? '';

    const payload: {
      organization: string;
      project: string;
      project_number: string | null;
      project_lead?: string;
      program?: string;
      is_active: boolean;
      old_organization?: string;
    } = {
      organization: newOrganization,
      project: onPlatform.project ?? '',
      project_number: onPlatform.project_number ?? '',
      program: onPlatform.program ?? '',
      project_lead: onPlatform.project_lead ?? '',
      is_active,
    };
    if (opts?.old_organization != null && opts.old_organization.trim() !== '') {
      payload.old_organization = opts.old_organization.trim();
    }
    if (project_lead != null && project_lead !== '') {
      payload.project_lead = project_lead;
    }
    if (program != null && program !== '') {
      payload.program = program;
    }
    if (project_number != null && project_number !== '') {
      payload.project_number = project_number;
    }
    try {
      const data = await this.httpAuthHelperService.requestPromise(
        'POST',
        `${this.baseUrl}/admin-projects/update`,
        payload
      );
      if (data?.body?.success === false) {
        const error =
          data?.body?.error || data?.body?.message || 'Update failed';
        return { success: false, error };
      }
      return { success: true };
    } catch (err: unknown) {
      const message =
        (err as { error?: { error?: string }; message?: string })?.error
          ?.error ??
        (err as Error)?.message ??
        'Update failed';
      return { success: false, error: String(message) };
    }
  }

  async saveProjectLink(
    onPlatform: Project,
    replica: Project
  ): Promise<{ success: boolean; error?: string }> {
    return this.updateAdminProject(onPlatform, {
      ...replica,
      is_active: true,
    });
  }

  async removeProjectLink(
    onPlatform: Project
  ): Promise<{ success: boolean; error?: string }> {
    const payload = {
      organization: onPlatform.organization ?? '',
      project: onPlatform.project ?? '',
    };
    try {
      const data = await this.httpAuthHelperService.requestPromise(
        'POST',
        `${this.baseUrl}/admin-projects/remove-link`,
        payload
      );
      if (data?.body?.success === false) {
        const error =
          data?.body?.error || data?.body?.message || 'Remove link failed';
        return { success: false, error };
      }
      return { success: true };
    } catch (err: unknown) {
      const message =
        (err as { error?: { error?: string }; message?: string })?.error
          ?.error ??
        (err as Error)?.message ??
        'Remove link failed';
      return { success: false, error: String(message) };
    }
  }


  async changeProjectStatus(
    organization: string,
    project: string,
    is_active: boolean
  ): Promise<{ success: boolean; error?: string }> {
    const payload = {
      organization: organization?.trim() ?? '',
      project: project?.trim() ?? '',
      is_active,
    };
    try {
      const data = await this.httpAuthHelperService.requestPromise(
        'POST',
        `${this.baseUrl}/admin-projects/status/change`,
        payload
      );
      if (data?.body?.success === false) {
        const error =
          data?.body?.error || data?.body?.message || 'Status change failed';
        return { success: false, error };
      }
      return { success: true };
    } catch (err: unknown) {
      const message =
        (err as { error?: { error?: string }; message?: string })?.error
          ?.error ??
        (err as Error)?.message ??
        'Status change failed';
      return { success: false, error: String(message) };
    }
  }
}
